import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card";
import { Button } from "@/components/shadcn/button";
import { Badge } from "@/components/shadcn/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/shadcn/table";
import { Icon } from "@/components/Icon";
import { useChatStore } from "@/components/chat/store/chat";
import { useImagesStore } from "@/store/images";
import { Model } from "@/components/model-selector/types";
import { constants } from "@/constants";
import { SessionsChart } from "@/pages/dashboard/SessionsChart.tsx";

interface QuickAction {
  title: string;
  description: string;
  icon: string;
  badge?: string;
  action: () => void;
  gradient: string;
}

interface StatsCard {
  title: string;
  value: string;
  change: string;
  icon: string;
  color: string;
}

export const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setDefaultModelID } = useChatStore();
  const { setCurrentModel } = useImagesStore();

  const quickActions: QuickAction[] = [
    {
      title: "Start New Chat",
      description: "Chat with latest AI models",
      icon: "heroicons:chat-bubble-left-right",
      badge: "GPT-4",
      action: () => {
        setDefaultModelID("openai/gpt-4o");
        navigate("/chat");
      },
      gradient: "from-blue-500 to-cyan-400",
    },
    {
      title: "Generate Image",
      description: "Create stunning visuals with AI",
      icon: "heroicons:photo",
      badge: "DALL-E",
      action: () => {
        setCurrentModel({ id: "openai/image-1-high" } as Model);
        navigate("/studio");
      },
      gradient: "from-purple-500 to-pink-400",
    },
    {
      title: "Create Video",
      description: "Generate videos from text",
      icon: "heroicons:video-camera",
      badge: "New",
      action: () => {
        setCurrentModel({ id: "google/veo-3-fast" } as Model);
        navigate("/studio");
      },
      gradient: "from-green-500 to-teal-400",
    },
    {
      title: "Build Chatbot",
      description: "Custom AI assistant for your business",
      icon: "heroicons:cpu-chip",
      action: () => navigate("/my-assistants"),
      gradient: "from-orange-500 to-red-400",
    },
  ];

  const statsCards: StatsCard[] = [
    {
      title: "Total Chats",
      value: "1,234",
      change: "+12%",
      icon: "heroicons:chat-bubble-left-ellipsis",
      color: "text-blue-600",
    },
    {
      title: "Images Generated",
      value: "567",
      change: "+8%",
      icon: "heroicons:photo",
      color: "text-purple-600",
    },
    {
      title: "Chatbots Created",
      value: "23",
      change: "+15%",
      icon: "heroicons:cpu-chip",
      color: "text-green-600",
    },
    {
      title: "Credits Used",
      value: "89,420",
      change: "+3%",
      icon: "heroicons:currency-dollar",
      color: "text-orange-600",
    },
  ];

  const recentModels = [
    { name: "GPT-4 Turbo", provider: "OpenAI", lastUsed: "2 hours ago" },
    {
      name: "Claude 3.5 Sonnet",
      provider: "Anthropic",
      lastUsed: "5 hours ago",
    },
    { name: "Gemini Pro", provider: "Google", lastUsed: "1 day ago" },
    { name: "DALL-E 3", provider: "OpenAI", lastUsed: "3 hours ago" },
  ];

  const sessionsData = [
    {
      status: "Active",
      started: "2024-01-15 14:30:25",
      duration: "2h 15m",
      region: "US-East",
      sessionId: "ses_abc123456",
    },
    {
      status: "Completed",
      started: "2024-01-15 12:45:10",
      duration: "45m 30s",
      region: "EU-Central",
      sessionId: "ses_def789012",
    },
    {
      status: "Failed",
      started: "2024-01-15 11:20:15",
      duration: "5m 12s",
      region: "AP-Southeast",
      sessionId: "ses_ghi345678",
    },
    {
      status: "Active",
      started: "2024-01-15 10:15:30",
      duration: "3h 45m",
      region: "US-West",
      sessionId: "ses_jkl901234",
    },
    {
      status: "Completed",
      started: "2024-01-15 09:30:45",
      duration: "1h 20m",
      region: "EU-West",
      sessionId: "ses_mno567890",
    },
  ];

  return (
    <div className="space-y-6">
      {/* AI Assistant CTA */}
      <Button
        className="w-full h-auto p-4 text-white justify-start"
        onClick={() => navigate("/chat")}
      >
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
          <Icon icon="heroicons:cpu-chip" className="h-5 w-5 text-white" />
        </div>
        <div className="text-left">
          <h3 className="font-semibold text-xl text-white">AI Assistant</h3>
          <p className="text-sm text-blue-100">
            BrowserCloud AI will help with any integration questions
          </p>
        </div>
      </Button>

      <SessionsChart />

      {/* Sessions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Icon icon="heroicons:table-cells" className="h-5 w-5 mr-2" />
            Recent Sessions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Region</TableHead>
                <TableHead>Session ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessionsData.map((session, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Badge
                      variant={
                        session.status === "Active"
                          ? "default"
                          : session.status === "Completed"
                          ? "secondary"
                          : "destructive"
                      }
                    >
                      {session.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {session.started}
                  </TableCell>
                  <TableCell>{session.duration}</TableCell>
                  <TableCell>{session.region}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {session.sessionId}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
